// src/components/Bookings/AllBookings.jsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Chip,
    CircularProgress,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Pagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAllBookings } from '../../Apis/BookingsApi';

const AllBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    // Controlled inputs
    const [searchInput, setSearchInput] = useState('');
    const [bookingType, setBookingType] = useState('');
    const [payment, setPayment] = useState('');
    const [status, setStatus] = useState('');

    // Applied filters
    const [appliedSearch, setAppliedSearch] = useState('');
    const [appliedBookingType, setAppliedBookingType] = useState('');
    const [appliedPayment, setAppliedPayment] = useState('');
    const [appliedStatus, setAppliedStatus] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        fetchBookings();
    }, [page, rowsPerPage, appliedSearch, appliedBookingType, appliedPayment, appliedStatus]);

    const fetchBookings = async () => {
        try {
            setLoading(true);
            const filters = {
                page,
                limit: rowsPerPage,
                bookingId: appliedSearch || undefined,
                bookingType: appliedBookingType || undefined,
                payment:
                    appliedPayment === 'paid'
                        ? true
                        : appliedPayment === 'pending'
                            ? false
                            : undefined,
                status: appliedStatus || undefined,
            };

            console.log('API Filters:', filters);

            const data = await getAllBookings(filters);
            setBookings(data.bookings || []);
            setTotalPages(data.totalPages || 1);
        } catch (error) {
            console.error('Failed to fetch bookings:', error);
        } finally {
            setLoading(false);
        }
    };

    const normalizeStatus = (val) =>
        val === true || val === 'true' || val === 1 || val === '1';

    const getPaymentLabel = (isPayment) =>
        normalizeStatus(isPayment) ? 'Paid' : 'Unpaid';

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'confirmed':
                return 'info';
            case 'cancelled':
                return 'error';
            case 'delivered':
                return 'success';
            default:
                return 'default';
        }
    };

    const handleSearch = () => {
        setAppliedSearch(searchInput.trim());
        setAppliedBookingType(bookingType);
        setAppliedPayment(payment);
        setAppliedStatus(status);
        setPage(1);
    };

    const handleReset = () => {
        setSearchInput('');
        setBookingType('');
        setPayment('');
        setStatus('');
        setAppliedSearch('');
        setAppliedBookingType('');
        setAppliedPayment('');
        setAppliedStatus('');
        setPage(1);
    };

    const handleChangePage = (event, value) => {
        setPage(value);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleViewDetails = (bookingId) => {
        navigate(`/bookings/${bookingId}`);
    };

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Booking List
            </Typography>

            {/* Filters Section */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField
                    label="Search Booking ID"
                    variant="outlined"
                    size="small"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Booking Type</InputLabel>
                    <Select
                        value={bookingType}
                        onChange={(e) => setBookingType(e.target.value)}
                        label="Booking Type"
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="offlinecut">Offline Cut</MenuItem>
                        <MenuItem value="onlinecut">Online Cut</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Payment</InputLabel>
                    <Select
                        value={payment}
                        onChange={(e) => setPayment(e.target.value)}
                        label="Payment"
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="pending">Unpaid</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        label="Status"
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="confirmed">Confirmed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="delivered">Delivered</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={handleSearch} sx={{ width: 150 }}>
                    Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ width: 150 }}>
                    Reset
                </Button>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <CircularProgress />
                </Box>
            ) : bookings.length === 0 ? (
                <Typography>No bookings found.</Typography>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>S.No</TableCell>
                                    <TableCell>Booking ID</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Booking Type</TableCell>
                                    <TableCell>Store ID</TableCell>
                                    <TableCell>Total Amount</TableCell>
                                    <TableCell>Payment Status</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bookings.map((booking, index) => (
                                    <TableRow key={booking.bookingId || booking._id}>
                                        <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>{booking.bookingId || '-'}</TableCell>
                                        <TableCell>{booking.user?.name || booking.userId || '-'}</TableCell>
                                        <TableCell>{booking.booking_type}</TableCell>
                                        <TableCell>{booking.storeId || '-'}</TableCell>
                                        <TableCell>{booking.total_amout}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getPaymentLabel(booking.isPayment)}
                                                color={normalizeStatus(booking.isPayment) ? 'success' : 'warning'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.status}
                                                color={getStatusColor(booking.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell> 
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleViewDetails(booking.bookingId)}
                                            >
                                                View Details
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination Section */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                                {[5, 10, 20, 50].map((num) => (
                                    <MenuItem key={num} value={num}>
                                        {num} Rows
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handleChangePage}
                            color="primary"
                            shape="rounded"
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AllBookings;
